import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, exhaustMap, switchMap, tap, withLatestFrom } from 'rxjs';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { PipelineRequestStatusEnum } from '../../enums/pipeline-request-status.enum';
import { MarginPipelineRequest } from '../../models/contract-request/margin-pipeline-request.model';
import { LocumsMarginContractRequestService } from '../../services/locums-margin-contract-request.service';
import { LocumsMarginCalculatorStore } from '../locums-margin-calculator.store';
import { LocumsMarginCalculatorSlice } from '../locums-margin-calculator.slice';
import { LocumsMarginCalculatorEvents } from 'src/app/recruiting/locums-margin-calculator/store/enums/locums-margin-calculator-events.enum';
import { LocumsContractRequestsEvents } from '../enums/locums-contract-request-events.enum';
import { EventBusService, EventPayload } from 'src/app/shared/services/event-bus.service';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';

@Injectable({ providedIn: 'root' })
export class LocumsMarginPipelineRequestEffects {
    private readonly eventBus = inject(EventBusService);
    private readonly contractRequestService = inject(LocumsMarginContractRequestService);
    private readonly marginCalculatorStore = inject(LocumsMarginCalculatorStore);
    private readonly toasterService = inject(ToasterService);
    private readonly _ldFeatureManager = inject(LDFeatureManager);

    private readonly novaLocumsLeave1099GmcDialog$ = this._ldFeatureManager.isEnabled(
        FeatureFlag.NovaLocumsLeave1099GmcDialog
    );

    constructor() {
        this.listenToLocums1099MarginPipelineRequestSaveButtonClickedEvent();
        this.listenToLocums1099MarginPendingRequestSaveButtonClickedEvent();
        this.listenToLocums1099MarginPipelineRequestCancelButtonClickedEvent();
        this.listenToLocums1099MarginPipelineRequestsPageOpenedEvent();
        this.listenToLocums1099MarginUpdatePendingRequestSaveButtonClickedEvent();
    }

    private listenToLocums1099MarginPipelineRequestSaveButtonClickedEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PIPELINE_REQUEST_SAVE_BUTTON_CLICKED)
            .subscribe(() => {
                this.saveMarginPipelineRequest({
                    slice: this.marginCalculatorStore.getSnapshot(),
                    reloadPipelineRequests: true
                });
            });
    }

    private listenToLocums1099MarginPendingRequestSaveButtonClickedEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PENDING_REQUEST_SAVE_BUTTON_CLICKED)
            .subscribe((event) => {
                const payload = event?.payload as
                    | {
                          status?: PipelineRequestStatusEnum;
                          isAccountManager?: boolean;
                          isRecruiter?: boolean;
                      }
                    | undefined;
                this.saveMarginPendingRequest({
                    slice: this.marginCalculatorStore.getSnapshot(),
                    status: payload?.status ?? PipelineRequestStatusEnum.PendingAmReview,
                    isAccountManager: payload?.isAccountManager,
                    isRecruiter: payload?.isRecruiter
                });
            });
    }

    private listenToLocums1099MarginPipelineRequestsPageOpenedEvent() {
        this.eventBus
            .on(LocumsContractRequestsEvents.LOCUMS_CONTRACT_REQUEST_PAGE_OPENED)
            .subscribe((event: EventPayload) => {
                this.getMarginPipelineRequests(event.payload.marginId);
            });
    }

    private listenToLocums1099MarginPipelineRequestCancelButtonClickedEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PIPELINE_REQUEST_CANCEL_BUTTON_CLICKED)
            .subscribe((event) => {
                this.cancelMarginPipelineRequest(event.payload.pipelineRequestId);
            });
    }

    private listenToLocums1099MarginUpdatePendingRequestSaveButtonClickedEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_UPDATE_PENDING_REQUEST_SAVE_BUTTON_CLICKED)
            .subscribe((event) => {
                const payload = event?.payload as
                    | {
                          pipelineRequestId: number;
                      }
                    | undefined;
                this.updateMarginPendingRequest({
                    slice: this.marginCalculatorStore.getSnapshot(),
                    pipelineRequestId: payload?.pipelineRequestId
                });
            });
    }

    readonly saveMarginPipelineRequest = rxMethod<{
        slice: LocumsMarginCalculatorSlice;
        reloadPipelineRequests?: boolean;
    }>((input$) =>
        input$.pipe(
            exhaustMap(({ slice, reloadPipelineRequests = false }) => {
                return this.contractRequestService.createContractRequest(slice).pipe(
                    withLatestFrom(this.novaLocumsLeave1099GmcDialog$),
                    tap(([savedId, isFormResetEnabled]) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PIPELINE_REQUEST_SAVE_SUCCEEDED,
                            payload: { id: savedId }
                        });
                        this.toasterService.toast('Pipeline saved.');
                        this.marginCalculatorStore.updateMarginId(savedId);
                        if (isFormResetEnabled) {
                            this.eventBus.emit({
                                type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_FORM_RESET
                            });
                        }
                        if (reloadPipelineRequests) {
                            this.getMarginPipelineRequests(savedId);
                        }
                    }),
                    catchError((error) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PIPELINE_REQUEST_SAVE_FAILED,
                            payload: { error }
                        });
                        this.toasterService.toast('Error saving pipeline.');
                        return EMPTY;
                    })
                );
            })
        )
    );

    readonly saveMarginPendingRequest = rxMethod<{
        slice: LocumsMarginCalculatorSlice;
        status: PipelineRequestStatusEnum;
        isAccountManager?: boolean;
        isRecruiter?: boolean;
    }>((input$) =>
        input$.pipe(
            exhaustMap(({ slice, status, isAccountManager, isRecruiter }) => {
                return this.contractRequestService.createPendingContractRequest(slice, status).pipe(
                    withLatestFrom(this.novaLocumsLeave1099GmcDialog$),
                    tap(([savedId, isFormResetEnabled]) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PENDING_REQUEST_SAVE_SUCCEEDED,
                            payload: { id: savedId }
                        });
                        this.toasterService.toast('Contract request sent successfully.');
                        this.marginCalculatorStore.updateMarginId(savedId);
                        // Call getMarginPipelineRequests after successful save
                        this.getMarginPipelineRequests(savedId);
                        if (isFormResetEnabled) {
                            this.eventBus.emit({
                                type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_FORM_RESET
                            });
                        }
                    }),
                    catchError((error) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PENDING_REQUEST_SAVE_FAILED,
                            payload: { error }
                        });
                        this.toasterService.toast('Error sending Pending Request.');
                        return EMPTY;
                    })
                );
            })
        )
    );

    readonly updateMarginPendingRequest = rxMethod<{
        slice: LocumsMarginCalculatorSlice;
        pipelineRequestId: number;
    }>((input$) =>
        input$.pipe(
            exhaustMap(({ slice, pipelineRequestId }) => {
                return this.contractRequestService.updatePendingAmContractRequest(slice, pipelineRequestId).pipe(
                    tap((savedId: number) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_UPDATE_PENDING_REQUEST_SAVE_SUCCEEDED,
                            payload: { id: savedId }
                        });
                        this.toasterService.toast('Contract request updated successfully.');
                        this.marginCalculatorStore.updateMarginId(savedId);
                        // Call getMarginPipelineRequests after successful save
                        this.getMarginPipelineRequests(savedId);
                    }),
                    catchError((error) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_UPDATE_PENDING_REQUEST_SAVE_FAILED,
                            payload: { error }
                        });
                        this.toasterService.toast('Error updating Pending Request.');
                        return EMPTY;
                    })
                );
            })
        )
    );

    readonly getMarginPipelineRequests = rxMethod<number>((marginId$) =>
        marginId$.pipe(
            switchMap((marginId) =>
                this.contractRequestService.getPipelineRequests(marginId).pipe(
                    tap((pipelineRequests: MarginPipelineRequest[]) => {
                        this.eventBus.emit({
                            type: LocumsContractRequestsEvents.LOCUMS_CONTRACT_REQUESTS_LOAD,
                            payload: { pipelineRequests }
                        });
                    }),
                    catchError(() => {
                        this.toasterService.toast('Error loading Margin Pipeline Requests');
                        return EMPTY;
                    })
                )
            )
        )
    );

    readonly cancelMarginPipelineRequest = rxMethod<number>((pipelineRequestId$) =>
        pipelineRequestId$.pipe(
            switchMap((pipelineRequestId) =>
                this.contractRequestService.cancelContractRequest(pipelineRequestId).pipe(
                    tap(() => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PIPELINE_REQUEST_CANCEL_SUCCEEDED,
                            payload: { id: pipelineRequestId }
                        });
                        this.toasterService.toast('This request has been canceled.');
                        // Call getMarginPipelineRequests after successful cancel
                        this.getMarginPipelineRequests(this.marginCalculatorStore.marginSetup().marginId);
                    }),
                    catchError((error) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PIPELINE_REQUEST_CANCEL_FAILED,
                            payload: { error }
                        });
                        this.toasterService.toast('Error canceling request.');
                        return EMPTY;
                    })
                )
            )
        )
    );
}
